import { ArrayTexture, Texture } from '../texture';
import { Context } from '../context/Context';

/**
 * Material shape for the `gbuffer-terrain` shader. Surface blending is driven by a
 * per-tile index + weight pair (built by SurfaceRegionRasterizer) rather than per-vertex
 * COLOR_0 weights. Up to 256 surfaces are supported via a sampler2DArray.
 *
 * Not a subclass of Material — the engine renderer branches on `instanceof TerrainMaterial`
 * to pick the terrain shader path.
 */
export class TerrainMaterial {
  /** sRGB N-layer TEXTURE_2D_ARRAY (required). Layer i = albedo for surface i. */
  albedoArray: ArrayTexture;

  /** Linear N-layer TEXTURE_2D_ARRAY (optional). Absent → flat geometric normal. */
  normalArray?: ArrayTexture;

  /** Linear N-layer TEXTURE_2D_ARRAY, grayscale in R (optional). Absent → [surfaceRoughnessFallback]. */
  roughnessArray?: ArrayTexture;

  /**
   * 1×N RGBA32F texture. Each texel holds per-surface scalar properties:
   *   r = triplanar tilingScale (world-units per texture tile)
   *   g = roughness fallback when roughnessArray is absent
   *   b, a = reserved
   * Sampled with texelFetch by layer index decoded from the index map.
   */
  surfacePropsTex: WebGLTexture;

  /** 1×1 RG8 NEAREST fallback indices: (0, 0) → fully surface 0. */
  trivialIndices: WebGLTexture;
  /** 1×1 R8 LINEAR fallback weight: 0 → fully A (= surface 0). */
  trivialWeight: WebGLTexture;

  /** Number of surfaces stored in the arrays (= number of texels in surfacePropsTex). */
  surfaceCount: number;

  /** Stable wrappers — same identity every frame so TextureState cache hits. */
  readonly surfacePropsTexWrapper: Texture;
  readonly trivialIndicesWrapper: Texture;
  readonly trivialWeightWrapper: Texture;

  constructor(
    albedoArray: ArrayTexture,
    surfacePropsTex: WebGLTexture,
    surfaceCount: number,
    gl: WebGL2RenderingContext,
  ) {
    this.albedoArray = albedoArray;
    this.surfacePropsTex = surfacePropsTex;
    this.surfaceCount = surfaceCount;
    this.trivialIndices = createTrivialIndices(gl);
    this.trivialWeight = createTrivialWeight(gl);
    this.surfacePropsTexWrapper = Texture.fromWebGlTexture(surfacePropsTex);
    this.trivialIndicesWrapper = Texture.fromWebGlTexture(this.trivialIndices);
    this.trivialWeightWrapper = Texture.fromWebGlTexture(this.trivialWeight);
  }

  /**
   * Deletes only the 1×1 trivial-fallback textures owned by this material. The
   * surface array textures and `surfacePropsTex` are owned by `SurfaceTextureManager`
   * (which constructs them and feeds them into this material via the constructor) —
   * the manager destroys those on its own `destroy()`.
   */
  destroy(ctx: Context): void {
    const gl = ctx.gl;
    gl.deleteTexture(this.trivialIndices);
    gl.deleteTexture(this.trivialWeight);
  }
}

function createTrivialIndices(gl: WebGL2RenderingContext): WebGLTexture {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG8, 1, 1, 0, gl.RG, gl.UNSIGNED_BYTE, new Uint8Array([0, 0]));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function createTrivialWeight(gl: WebGL2RenderingContext): WebGLTexture {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  // Match the rasterizer's default-fill convention (weight=255 = "fully B").
  // The 1×1 fallback has A=B=0 so weight is visually irrelevant here, but
  // staying consistent avoids surprise if a future tile blends across the
  // trivial fallback.
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 1, 1, 0, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array([255]));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}
